import React, { ReactNode, useContext } from 'react'
import { useTranslation, TFunction } from 'react-i18next'
import { Form, Field } from 'react-final-form'
import { Button } from '@pokemon/design-v2.button'
import { TextField } from '@pokemon/design.ui.input.text-field'
import { LoadingContainer, LoadingContext } from '@pokemon/design.ui.loading-spinner'

export type CreateScreenNamePageProps = {
  /**
   * a node to be rendered in the special component.
   */
  children?: ReactNode;
   /**
   * a function to call when user clicks on the "skip button" 
   */ 
  onSkip: React.MouseEventHandler<HTMLButtonElement & HTMLAnchorElement> | undefined;
  /**
   * a boolean to determine weather to show account or child account text 
   */
  isChild: boolean;
  /**
   * an async function to use for checking if screenname is clean and taken  
   */
  checkScreenname: (screenname: string) => Promise<string | undefined>;
  /**
   * an function for handling errors  
   */
  setError: (error: string) => void;
  /**
   * an function called once the screenname has been validated  
   */
  onScreennameCleared: (screenname: string) => void;
};

const validations = {
  validateNotBlank: (screenname: string) => {
    if (screenname.length === 0) {
      return false
    }

    let idx = screenname.length
    while (idx--) {
      const curr = screenname.charAt(idx)
      if (curr !== ' ' && curr !== '\n' && curr !== '\t' && curr !== '\n' && curr !== '\r') {
        return true
      }
    }

    return false
  },
}

function minLengthMessage (t: TFunction) {
  return t('screenname.min_length', 'Screen name must be no less than 3 characters long.')
}

function maxLengthMessage (t: TFunction) {
  return t('screenname.max_length', 'Screen name must be no more than 15 characters long')
}

function charactersMessage (t: TFunction) {
  return t('screenname.characters', 'Screen name must only contain valid characters')
}

export function validateScreenname (t: TFunction) {
  return (screenname: string) => {
    if (!screenname || !validations.validateNotBlank(screenname)) {
      return t('screenname.required', 'No screen name entered.')
    } else if (screenname.length < 3) {
      return minLengthMessage(t)
    } else if (screenname.length > 15) {
      return maxLengthMessage(t)
    } else if ((screenname.charAt(0) === '-') || (screenname.charAt(screenname.length - 1) === '-')) {
      return t('screenname.dashes', 'Screen name can not begin or end with a dash')
    } else if (!/^[0-9a-zA-ZÀ-ÿ_-]+$/.test(screenname)) {
      return charactersMessage(t)
    } else {
      return null
    }
  }
}

export function getMessageFromScreennameErrors (t: TFunction, screenNameErrors: any[]): string | undefined {
  if (screenNameErrors.includes('min_length')) {
    return minLengthMessage(t)
  } else if (screenNameErrors.includes('max_length')) {
    return maxLengthMessage(t)
  } else if (screenNameErrors.includes('invalid')) {
    return charactersMessage(t)
  } else if (screenNameErrors.includes('not_clean')) {
    return t('screenname.cleanspeak_invalid', 'Your screen name contains inappropriate language.')
  } else if (screenNameErrors.includes('in_use')) {
    return t('screenname.server_invalid', 'Your screen name is already taken.')
  } else {
    return undefined
  }
}

export function CreateScreenNamePage ({ checkScreenname, onScreennameCleared, setError, onSkip, isChild }: CreateScreenNamePageProps) {
  const { t } = useTranslation()
  const { setLoading } = useContext(LoadingContext)
  const onSubmit = (t, setLoading) => async (values, _, callback) => {
    setLoading(true)
    const checkResult = await checkScreenname(values.screen_name)
    let callbackResult: any = undefined
    if (checkResult === 'cleared') {
      onScreennameCleared(values.screen_name)
    } else {
      const message = getMessageFromScreennameErrors(t, [checkResult])
      if (message) {
        callbackResult = { screen_name: message }
      } else {
        setError(t('error_could_not_check_screenname', 'An unexpected error occured, please try again later.'))
      }
    }
    callback(callbackResult)
    setLoading(false)
  }

  return <Form onSubmit={onSubmit(t, setLoading)}
      render={
        ({ handleSubmit, submitting, hasSubmitErrors, hasValidationErrors, modifiedSinceLastSubmit }) => (
          <form onSubmit={handleSubmit}>
            <h1 className= 'header'>{ isChild ? t('screenname_select_header_child', 'Enter Your Child\'s Screen Name') : t('screenname_select_header', 'Enter Your Screen Name')}</h1>
            <label className='primary'>{ isChild ? t('screennname_select_label_child', 'Your child\'s screen name is used to identify them in public areas of the website, certain games, and leaderboards.') : t('screennname_select_label', 'Your screen name is used to identify you in public areas of the website, certain games, and leaderboards.')}</label>
            <Field name="screen_name"
              validate={validateScreenname(t)}
            >
              {({ input, meta }) => (
                <React.Fragment>
                    <TextField {...input}
                      type="text"
                      placeholder={t('screenname_placeholder', 'Screen Name')}
                      errorText={(meta.error || (meta.submitError && !modifiedSinceLastSubmit)) && meta.touched && (meta.error || meta.submitError)}                    />
                </React.Fragment>
              )}
            </Field>
            <ul className = "bulleted">
                <li>{t('screen_name_character_requirement', 'name must be between 3-15 characters')}</li>
                <li>{t('screen_name_dashes_requirement', 'name cannot begin or end with a dash')}</li>
            </ul>
            <p className = "tertiary"> {t('screenname_suggestion', 'Try adding some numbers after the screen name to find one that is unique. Also, remember that screen names must be appropriate for this site. Inappropriate words and foul language are not permitted.')} </p>
            <p className = "emphasis"> {isChild ? t('screen_name_no_personal_info_child', 'try not to use any personal information in your child\'s screen name. this is not your child\'s username.') : t('screen_name_no_personal_info', 'try not to use any personal information in your screen name. this is not your username.')}</p>
            <Button style={{ width: '100%', marginTop: '32px' }}
                        type="submit"
                        data-testid="screen-name-page-submit-button"
                        disabled={submitting || hasValidationErrors || (hasSubmitErrors && !modifiedSinceLastSubmit)}
                        value="Continue"
                    >
              {t('continue_button', 'Continue')}
            </Button>
            <Button style={{ width: '100%', marginTop: '8.75px' }}
                        type="button"
                        id="screennameSkip"
                        data-testid="screen-name-page-skip-button"
                        variant={'outlined'}
                        value="Skip"
                        disabled={submitting}
                        onClick={onSkip}
                    >
              {t('skip_button', 'Skip')}
            </Button>
          </form>
        )
      } />
}
